import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

function PrivacyPolicy() {
  const sectionStyle = { fontSize: '1.75rem', fontWeight: '700', color: '#7c3aed', marginTop: '2.5rem', marginBottom: '1.5rem' };
  const subSectionStyle = { fontSize: '1.35rem', fontWeight: '600', color: '#1f2937', marginTop: '2rem', marginBottom: '1rem' };
  const paragraphStyle = { fontSize: '1.05rem', lineHeight: '1.8', color: '#4b5563', marginBottom: '1.5rem' };
  const listStyle = { fontSize: '1.05rem', lineHeight: '2', color: '#4b5563', paddingLeft: '2rem', marginBottom: '1.5rem' };
  const noticeStyle = { background: '#fef3c7', padding: '1.5rem', borderRadius: '8px', border: '2px solid #fbbf24', marginBottom: '1.5rem' };

  return (
    <>
      <Helmet>
        <title>Privacy Policy - TEJ Solutions Hub</title>
        <meta name="description" content="Privacy Policy for TEJ Solutions Hub - How we collect, use, and protect your information across all our services." />
      </Helmet>
      
      <div style={{ paddingTop: '100px', background: '#f9fafb', minHeight: '100vh' }}>
        <div style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)', padding: '4rem 2rem', color: 'white', textAlign: 'center' }}>
          <h1 style={{ fontSize: '3rem', fontWeight: '800', marginBottom: '0.5rem' }}>Privacy Policy</h1>
          <p style={{ fontSize: '1.1rem', opacity: '0.95', marginBottom: '0.5rem' }}>TEJ SUSTAINABILITY GROUP, INC.</p>
          <p style={{ fontSize: '0.95rem', opacity: '0.85' }}>Last Updated: December 1, 2025</p>
        </div>

        <div style={{ maxWidth: '900px', margin: '0 auto', padding: '4rem 2rem' }}>
          <div style={{ background: 'white', padding: '3rem', borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
            
            <p style={paragraphStyle}>
              TEJ SUSTAINABILITY GROUP, INC., doing business as TEJ Solutions Hub ("Company," "we," "our," or "us"), is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website, software development services, digital marketing services, SMS/email campaigns, cloud infrastructure solutions, and business financing referral services.
            </p>
            <p style={paragraphStyle}>
              By using our services or website (tejsolutionshub.com), you agree to the collection and use of information in accordance with this Privacy Policy.
            </p>

            <h2 style={sectionStyle}>1. Information We Collect</h2>

            <h3 style={subSectionStyle}>1.1 Personal Information</h3>
            <p style={paragraphStyle}>We may collect the following personal information:</p>
            <ul style={listStyle}>
              <li>Name, email address, phone number</li>
              <li>Company name and business information</li>
              <li>Billing and payment information</li>
              <li>Project requirements and specifications</li>
              <li>Communication preferences</li>
              <li>Technical support inquiries</li>
            </ul>

            <h3 style={subSectionStyle}>1.2 Customer Communication Information</h3>
            <p style={paragraphStyle}>
              We collect contact information from our customers and clients to communicate about our services, projects, and business matters. This includes phone numbers and email addresses used for direct business communication.
            </p>
            <div style={noticeStyle}>
              <p style={{ fontSize: '1rem', color: '#92400e', fontWeight: '600', marginBottom: '0.5rem' }}>Important:</p>
              <p style={{ fontSize: '1rem', color: '#78350f', lineHeight: '1.6', marginBottom: '0' }}>
                Contact information collected on tejsolutionshub.com is used for business consultation and customer communication purposes. We obtain proper consent before sending SMS or email communications to our customers.
              </p>
            </div>

            <h3 style={subSectionStyle}>1.3 Technical Information</h3>
            <p style={paragraphStyle}>We automatically collect certain technical information:</p>
            <ul style={listStyle}>
              <li>IP address and device information</li>
              <li>Browser type and version</li>
              <li>Operating system</li>
              <li>Pages visited and time spent</li>
              <li>Referring website addresses</li>
              <li>Cookies and similar tracking technologies</li>
            </ul>

            <h3 style={subSectionStyle}>1.4 Development Project Data</h3>
            <p style={paragraphStyle}>For web and mobile app development projects, we may collect:</p>
            <ul style={listStyle}>
              <li>Project specifications and requirements</li>
              <li>Design assets and content</li>
              <li>Source code and technical documentation</li>
              <li>Testing and quality assurance data</li>
              <li>Deployment and hosting credentials (securely stored)</li>
            </ul>

            <h3 style={subSectionStyle}>1.5 Business Financing Information</h3>
            <p style={paragraphStyle}>For business financing referral services, we may collect:</p>
            <ul style={listStyle}>
              <li>Business name and industry</li>
              <li>Annual revenue and time in business</li>
              <li>Financing needs and amount requested</li>
              <li>Business credit information (if provided)</li>
              <li>Financial statements (if provided)</li>
              <li>Contact information for financing inquiries</li>
            </ul>
            <div style={noticeStyle}>
              <p style={{ fontSize: '1rem', color: '#92400e', fontWeight: '600', marginBottom: '0.5rem' }}>Important:</p>
              <p style={{ fontSize: '1rem', color: '#78350f', lineHeight: '1.6', marginBottom: '0' }}>
                We are NOT a direct lender. We connect businesses with financing partners and lenders. Financial information is shared only with your consent and only with approved lending partners for the purpose of evaluating financing options.
              </p>
            </div>

            <h3 style={subSectionStyle}>1.6 Cloud Infrastructure Data</h3>
            <p style={paragraphStyle}>When managing cloud infrastructure for clients:</p>
            <ul style={listStyle}>
              <li>Cloud account credentials (encrypted)</li>
              <li>Server configurations and logs</li>
              <li>Performance metrics and analytics</li>
              <li>Security and backup information</li>
            </ul>

            <h2 style={sectionStyle}>2. How We Use Your Information</h2>

            <h3 style={subSectionStyle}>2.1 Service Delivery</h3>
            <ul style={listStyle}>
              <li>Develop and deliver web and mobile applications</li>
              <li>Execute digital marketing campaigns</li>
              <li>Send SMS/email communications to our customers</li>
              <li>Manage cloud infrastructure and DevOps</li>
              <li>Provide technical support and maintenance</li>
              <li>Process payments and billing</li>
            </ul>

            <h3 style={subSectionStyle}>2.2 Customer Communications</h3>
            <p style={paragraphStyle}>We use SMS and email to communicate with our customers for:</p>
            <ul style={listStyle}>
              <li>Project updates and notifications</li>
              <li>Service delivery and support</li>
              <li>Business consultations and inquiries</li>
              <li>Account and billing information</li>
              <li>Marketing our services (with consent)</li>
            </ul>

            <h3 style={subSectionStyle}>2.3 Digital Marketing Services</h3>
            <ul style={listStyle}>
              <li>SEO optimization and content marketing</li>
              <li>Social media campaign management</li>
              <li>PPC advertising and analytics</li>
              <li>Email marketing automation</li>
              <li>Performance tracking and reporting</li>
            </ul>

            <h3 style={subSectionStyle}>2.4 Business Financing Referral Services</h3>
            <p style={paragraphStyle}>When you inquire about business financing:</p>
            <ul style={listStyle}>
              <li>Connect you with approved lending partners</li>
              <li>Share your information with lenders (with consent)</li>
              <li>Coordinate financing application process</li>
              <li>Provide updates on financing status</li>
              <li>Facilitate communication between you and lenders</li>
            </ul>
            <div style={noticeStyle}>
              <p style={{ fontSize: '1rem', color: '#92400e', fontWeight: '600', marginBottom: '0' }}>
                We do NOT make lending decisions. We are a referral service connecting businesses with financing partners. All lending decisions are made by the lending partners.
              </p>
            </div>

            <h3 style={subSectionStyle}>2.5 Communication and Support</h3>
            <ul style={listStyle}>
              <li>Respond to inquiries and support requests</li>
              <li>Send project updates and notifications</li>
              <li>Provide technical documentation</li>
              <li>Conduct client meetings and consultations</li>
            </ul>

            <div style={noticeStyle}>
              <p style={{ fontSize: '1rem', color: '#92400e', fontWeight: '600', marginBottom: '0' }}>
                We do not sell, rent, or trade personal information to third parties for their marketing purposes.
              </p>
            </div>

            <h2 style={sectionStyle}>3. SMS/Email Communication Compliance</h2>

            <h3 style={subSectionStyle}>3.1 Our Compliance Commitment</h3>
            <p style={paragraphStyle}>
              We are committed to complying with all applicable regulations when communicating with our customers:
            </p>
            <ul style={listStyle}>
              <li>Obtaining valid opt-in consent before sending SMS or marketing emails</li>
              <li>Maintaining documentation of all consent</li>
              <li>Ensuring compliance with TCPA, CAN-SPAM, and CTIA regulations</li>
              <li>Providing clear opt-out mechanisms in all communications</li>
              <li>Honoring opt-out requests promptly</li>
            </ul>

            <h3 style={subSectionStyle}>3.2 How We Use Twilio</h3>
            <p style={paragraphStyle}>
              We use Twilio as our messaging platform to communicate with our customers. We:
            </p>
            <ul style={listStyle}>
              <li>Send messages directly to our customers via Twilio</li>
              <li>Implement opt-out mechanisms (STOP/UNSUBSCRIBE)</li>
              <li>Maintain message delivery logs for compliance</li>
              <li>Follow Twilio's terms of service and acceptable use policies</li>
              <li>Comply with all carrier and regulatory requirements</li>
            </ul>

            <h3 style={subSectionStyle}>3.3 Your Opt-Out Rights</h3>
            <p style={paragraphStyle}>You can opt out of our communications at any time:</p>
            <ul style={listStyle}>
              <li>Reply STOP to opt out of SMS messages</li>
              <li>Click unsubscribe links in emails</li>
              <li>Contact us directly at contact@tejsolutionshub.com</li>
            </ul>
            <p style={paragraphStyle}>
              Opt-out requests are processed immediately, though it may take up to 24 hours for changes to take effect.
            </p>

            <h2 style={sectionStyle}>4. Information Sharing and Disclosure</h2>

            <h3 style={subSectionStyle}>4.1 Third-Party Services and Partners</h3>
            <p style={paragraphStyle}>We may share information with:</p>
            <ul style={listStyle}>
              <li>Cloud hosting providers (AWS, Azure, GCP)</li>
              <li>SMS/email delivery platforms (Twilio, SendGrid, etc.)</li>
              <li>Payment processors</li>
              <li>Analytics and monitoring tools</li>
              <li>Development and testing platforms</li>
              <li><strong>Lending partners and financial institutions</strong> (only for financing referrals, with your consent)</li>
            </ul>
            <div style={noticeStyle}>
              <p style={{ fontSize: '1rem', color: '#92400e', fontWeight: '600', marginBottom: '0.5rem' }}>Financing Referrals:</p>
              <p style={{ fontSize: '1rem', color: '#78350f', lineHeight: '1.6', marginBottom: '0' }}>
                When you request business financing information, we share your business and contact information with approved lending partners to evaluate financing options. You consent to this sharing when you submit a financing inquiry. Lending partners have their own privacy policies.
              </p>
            </div>

            <h3 style={subSectionStyle}>4.2 Client Projects</h3>
            <p style={paragraphStyle}>
              Information related to client projects is shared only as necessary to deliver services and only with the client's authorization.
            </p>

            <h3 style={subSectionStyle}>4.3 Legal Requirements</h3>
            <p style={paragraphStyle}>We may disclose information when required to:</p>
            <ul style={listStyle}>
              <li>Comply with legal obligations</li>
              <li>Respond to lawful requests from authorities</li>
              <li>Protect our rights and property</li>
              <li>Prevent fraud or security threats</li>
              <li>Protect the safety of users</li>
            </ul>

            <h2 style={sectionStyle}>5. Data Security</h2>
            <p style={paragraphStyle}>We implement comprehensive security measures:</p>
            <ul style={listStyle}>
              <li>SSL/TLS encryption for data transmission</li>
              <li>Encrypted storage for sensitive information</li>
              <li>Access controls and authentication</li>
              <li>Regular security audits and updates</li>
              <li>Secure development practices</li>
              <li>Cloud infrastructure security (firewalls, monitoring)</li>
              <li>Regular backups and disaster recovery</li>
            </ul>
            <p style={paragraphStyle}>
              While we use industry-standard security measures, no system is 100% secure. We continuously update our security practices to protect your information.
            </p>

            <h2 style={sectionStyle}>6. Data Retention</h2>
            <p style={paragraphStyle}>We retain information for different periods based on purpose:</p>
            <ul style={listStyle}>
              <li><strong>Project Data:</strong> Duration of project plus 2 years for support</li>
              <li><strong>SMS/Email Logs:</strong> As required by telecom and email regulations (typically 1-3 years)</li>
              <li><strong>Opt-in/Opt-out Records:</strong> As required by TCPA and CAN-SPAM (minimum 4 years)</li>
              <li><strong>Financial Records:</strong> 7 years for tax and accounting purposes</li>
              <li><strong>Cloud Infrastructure Logs:</strong> 90 days to 1 year depending on service</li>
            </ul>

            <h2 style={sectionStyle}>7. Cookies and Tracking</h2>
            <p style={paragraphStyle}>We use cookies and similar technologies for:</p>
            <ul style={listStyle}>
              <li>Website functionality and user preferences</li>
              <li>Analytics and performance monitoring</li>
              <li>Marketing campaign effectiveness</li>
              <li>Security and fraud prevention</li>
            </ul>
            <p style={paragraphStyle}>
              You can control cookies through your browser settings. Disabling cookies may affect website functionality.
            </p>

            <h2 style={sectionStyle}>8. Your Rights</h2>

            <h3 style={subSectionStyle}>8.1 General Rights</h3>
            <p style={paragraphStyle}>You have the right to:</p>
            <ul style={listStyle}>
              <li>Access your personal information</li>
              <li>Correct inaccurate information</li>
              <li>Request deletion of your information</li>
              <li>Object to processing of your information</li>
              <li>Request data portability</li>
              <li>Withdraw consent</li>
            </ul>

            <h3 style={subSectionStyle}>8.2 California Residents (CCPA)</h3>
            <p style={paragraphStyle}>California residents have additional rights:</p>
            <ul style={listStyle}>
              <li>Know what personal information is collected</li>
              <li>Know if personal information is sold or disclosed</li>
              <li>Opt-out of sale of personal information</li>
              <li>Request deletion of personal information</li>
              <li>Non-discrimination for exercising rights</li>
            </ul>
            <div style={noticeStyle}>
              <p style={{ fontSize: '1rem', color: '#92400e', fontWeight: '600', marginBottom: '0' }}>
                We do not sell personal information.
              </p>
            </div>

            <h3 style={subSectionStyle}>8.3 EU Residents (GDPR)</h3>
            <p style={paragraphStyle}>If you are in the EU, you have rights under GDPR:</p>
            <ul style={listStyle}>
              <li>Right to access and data portability</li>
              <li>Right to rectification and erasure</li>
              <li>Right to restrict processing</li>
              <li>Right to object to processing</li>
              <li>Right to withdraw consent</li>
              <li>Right to lodge a complaint with supervisory authority</li>
            </ul>
            <p style={paragraphStyle}>
              Our legal basis for processing includes: contract performance, legal obligations, legitimate interests, and consent.
            </p>

            <h2 style={sectionStyle}>9. Children's Privacy</h2>
            <p style={paragraphStyle}>
              Our services are not intended for individuals under 18 years of age. We do not knowingly collect personal information from children. If we discover we have collected information from a child, we will delete it promptly.
            </p>

            <h2 style={sectionStyle}>10. International Data Transfers</h2>
            <p style={paragraphStyle}>
              Your information may be transferred to and processed in the United States and other countries where we or our third-party services operate. We ensure appropriate safeguards are in place for international transfers.
            </p>

            <h2 style={sectionStyle}>11. Third-Party Links</h2>
            <p style={paragraphStyle}>
              Our website and services may contain links to third-party websites. We are not responsible for the privacy practices of these external sites. We encourage you to review their privacy policies.
            </p>

            <h2 style={sectionStyle}>12. Changes to This Policy</h2>
            <p style={paragraphStyle}>
              We may update this Privacy Policy periodically. Changes will be posted on this page with an updated "Last Updated" date. Continued use of our services after changes constitutes acceptance of the updated policy.
            </p>

            <h2 style={sectionStyle}>13. Contact Information</h2>
            <p style={paragraphStyle}>For privacy-related questions or to exercise your rights, contact us:</p>
            <div style={{ background: '#f9fafb', padding: '1.5rem', borderRadius: '8px', marginTop: '1rem' }}>
              <p style={{ fontSize: '1.05rem', color: '#1f2937', lineHeight: '1.8', marginBottom: '0' }}>
                <strong>TEJ SUSTAINABILITY GROUP, INC.</strong> (d/b/a TEJ Solutions Hub)<br />
                4273 Savannah Trce, Atlanta, GA 30349<br />
                Email: <strong>contact@tejsolutionshub.com</strong><br />
                EIN: 86-3326707
              </p>
            </div>

            <h2 style={sectionStyle}>14. Service-Specific Privacy Notices</h2>

            <h3 style={subSectionStyle}>14.1 Web/Mobile Development</h3>
            <p style={paragraphStyle}>
              Project data, source code, and credentials are stored securely and accessed only by authorized team members. Upon project completion, we can transfer or delete data per client request.
            </p>

            <h3 style={subSectionStyle}>14.2 Cloud Infrastructure Management</h3>
            <p style={paragraphStyle}>
              Cloud credentials are encrypted and stored in secure vaults. Access is logged and monitored. We follow principle of least privilege for all cloud access.
            </p>

            <h3 style={subSectionStyle}>14.3 Digital Marketing Services</h3>
            <p style={paragraphStyle}>
              Marketing analytics data is used solely for campaign optimization and reporting. We comply with platform-specific privacy policies (Google, Facebook, etc.).
            </p>

            <h3 style={subSectionStyle}>14.4 SMS/Email Communications</h3>
            <p style={paragraphStyle}>
              All customer communications are processed according to TCPA, CAN-SPAM, and CTIA requirements. Opt-out lists are maintained and honored. Message logs are retained for compliance auditing.
            </p>

          </div>
        </div>
      </div>
    </>
  );
}

export default PrivacyPolicy;
